// The durable **sandbox credential** store (rounds-slice implementation-review **F1**, over the
// server-side **F2** hardening).
//
// This is the single most consequential piece of client persistence in the slice. Identity on the
// rounds surface is no longer derived from a display name — names are not secret, so nothing about
// a name may be load-bearing — and the credential `POST /rounds/session` issues is an unguessable
// 256-bit secret that is **the only way back into an existing sandbox**. A client that does not
// keep it silently gets a brand-new empty sandbox on every refresh, and the player loses their
// programs and their runs with no error to explain it. There is no account model to fall back on
// (DESIGN §15 defers one).
//
// It therefore follows the versioned-storage pattern the client already uses for durable,
// out-of-store preferences (`audio/audio-prefs-store.ts`, itself modelled on `session-store.ts`):
// a `StorageLike` shim accepted as a parameter (production passes a `window.localStorage` shim,
// tests pass an in-memory or throwing double), try/catch around **every** storage call, strict
// runtime validation of the untrusted stored value, degrade-to-absent rather than throw, and a
// `version` field so a future shape change is a migration rather than a silent misread.
//
// `localStorage`, not `sessionStorage`: a sandbox outlives the tab that opened it, and losing the
// credential when a tab closes would be the same data loss as never storing it.

/** The subset of the DOM `Storage` API this adapter consumes — the shared shape
 * `audio-prefs-store.ts` and `session-store.ts` also use, so any of the existing test doubles
 * is a drop-in. */
export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** The `localStorage` key this adapter owns. Distinct from `waggle-session` (wrong scope: that
 * one is tab-scoped `sessionStorage` for a live match) and from `waggle-audio-prefs`. */
export const ROUNDS_CREDENTIAL_KEY = 'waggle-rounds-credential';

/** The current stored-shape version. Any other value is treated as foreign/legacy and degrades
 * to "nothing stored" until a migration is written. */
export const ROUNDS_CREDENTIAL_VERSION = 1;

/** A sandbox credential is 32 bytes, hex — 64 characters. Validated locally so a truncated or
 * mangled value is discarded here instead of being presented and refused as
 * `400 invalid_credential`. */
const CREDENTIAL_PATTERN = /^[0-9a-f]{64}$/i;

/** What the client keeps between visits. The credential is the load-bearing field; the sandbox
 * scope and display name are conveniences (a friendlier first render, and a re-mint that keeps
 * the player's own name) and are never used to *select* a sandbox. */
export interface RoundsCredentialRecord {
  version: number;
  /** The 256-bit sandbox secret, hex. */
  credential: string;
  /** The last display name minted with. */
  displayName: string;
  /** The sandbox scope the credential last resolved to, e.g. `sandbox-r0-p1`. */
  sandbox: string;
}

/**
 * Runtime-validates an untrusted parsed value, returning a fresh well-typed record or `null` on
 * ANY failure (mirrors `validateAudioPrefs` / `validatePersistedSession`). A stored `null`, an
 * array, a wrong-typed field, a non-hex or wrong-length credential, or a missing/foreign
 * `version` all return `null` — which is what lets a malformed value degrade to "no credential"
 * rather than being presented to the server.
 */
export function validateRoundsCredential(value: unknown): RoundsCredentialRecord | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (record.version !== ROUNDS_CREDENTIAL_VERSION) {
    return null;
  }
  if (typeof record.credential !== 'string' || !CREDENTIAL_PATTERN.test(record.credential)) {
    return null;
  }
  if (typeof record.displayName !== 'string' || typeof record.sandbox !== 'string') {
    return null;
  }
  return {
    version: ROUNDS_CREDENTIAL_VERSION,
    credential: record.credential,
    displayName: record.displayName,
    sandbox: record.sandbox,
  };
}

/** The credential store seam. Never throws to the caller. */
export interface RoundsCredentialStore {
  /** The stored record, or `null` when nothing valid is stored (a first visit, a cleared
   * browser, private mode, a poisoned value). */
  get(): RoundsCredentialRecord | null;
  /** Persists the credential the server just issued. Best-effort: a failed write means the
   * credential does not survive this tab, never a thrown error mid-boot. */
  set(record: Omit<RoundsCredentialRecord, 'version'>): void;
  /** Forgets the stored credential — used when the server refuses it, so a rejected value is
   * never presented twice. */
  clear(): void;
}

/**
 * Builds the guarded credential store. Unlike `audio-prefs-store`, `get()` re-reads storage each
 * call rather than caching at construction: a boot reads it exactly once, and a cached copy would
 * only create a way for the in-memory and durable values to disagree about the one secret that
 * cannot be re-derived.
 */
export function createRoundsCredentialStore(
  storage: StorageLike,
  key: string = ROUNDS_CREDENTIAL_KEY,
): RoundsCredentialStore {
  return {
    get(): RoundsCredentialRecord | null {
      let raw: string | null;
      try {
        raw = storage.getItem(key);
      } catch {
        return null; // storage unavailable (private mode, a sandboxed SecurityError)
      }
      if (raw === null) {
        return null;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        return null; // malformed JSON — treated as nothing stored, never repaired in place
      }
      return validateRoundsCredential(parsed);
    },
    set(record): void {
      const stored: RoundsCredentialRecord = { version: ROUNDS_CREDENTIAL_VERSION, ...record };
      try {
        storage.setItem(key, JSON.stringify(stored));
      } catch {
        // Quota, private mode, sandboxed: the sandbox simply is not recoverable after this
        // tab closes. Losing the credential is bad; failing the boot over it is worse.
      }
    },
    clear(): void {
      try {
        storage.removeItem(key);
      } catch {
        // Nothing to do: a value we cannot remove is still one we will not present again this
        // load, because the caller drops it from memory too.
      }
    },
  };
}
